import React from 'react'
import styled, { css } from 'styled-components'
import { easeInOutTransition } from '~/components/CommonStyles'
import { dark, dimensions, light, spacing } from '~/constants/theme'

import Text, { FontStyle } from './Text'

const StyledButton = styled.button<{ borderShape; size; type }>`
  border-radius: ${dimensions.borderRadiusLG};
  align-items: center;
  ${easeInOutTransition};
  border: none;
  ${({ borderShape }) => {
    if (borderShape === 'rounded') {
      return css`
        border-radius: ${dimensions.borderRadiusLG};
      `
    }
    if (borderShape === 'square') {
      return css`
        border-radius: ${dimensions.borderRadiusSM};
      `
    }
  }}
  ${({ size }) => {
    if (size === 'normal') {
      return css`
        padding: ${spacing.spacingLG} ${spacing.spacingXL};
      `
    }
    if (size === 'small') {
      return css`
        padding: ${spacing.spacingMD} ${spacing.spacingLG};
      `
    }
  }}
  ${({ type }) => {
    if (type === 'primary') {
      return css`
        & p {
          color: ${light.gray100};
        }
        background-color: ${light.primary};
      `
    }
    if (type === 'secondary') {
      return css`
        & p {
          color: ${light.dark900};
        }
        background-color: transparent;
      `
    }
    if (type === 'positive') {
      return css`
        & p {
          color: ${light.green200};
        }
        background-color: ${dark.green200};
      `
    }
    if (type === 'negative') {
      return css`
        & p {
          color: ${light.red300};
        }
        background-color: ${light.redAlert};
      `
    }
    if (type === 'warning') {
      return css`
        & p {
          color: ${light.gray100};
        }
        background-color: ${light.red200};
      `
    }
    if (type === 'white') {
      return css`
        & p {
          color: ${light.dark900};
        }
        background-color: ${light.gray100};
      `
    }
  }}

  ${({ disabled }) =>
    disabled &&
    css`
      background-color: ${light.gray300};
    `}

  &:hover:enabled {
    ${({ type }) => {
      if (type === 'primary') {
        return css`
          background-color: ${light.primaryHover};
        `
      }
      if (type === 'secondary') {
        return css`
          background-color: ${light.gray300};
        `
      }
      if (type === 'positive') {
        return css`
          background-color: ${dark.green300};
        `
      }
      if (type === 'negative') {
        return css`
          background-color: ${light.redFocus};
        `
      }
      if (type === 'warning') {
        return css`
          background-color: ${light.red100};
        `
      }
      if (type === 'white') {
        return css`
          & p {
            color: ${light.dark900};
          }
          background-color: rgba(255, 255, 255, 0.8);
        `
      }
    }}
  }
  &:active:enabled {
    ${({ type }) => {
      if (type === 'primary') {
        return css`
          & p {
            color: ${light.dark600};
          }
          background-color: ${light.primary};
        `
      }
      if (type === 'secondary') {
        return css`
          background-color: ${light.gray400};
        `
      }
      if (type === 'warning') {
        return css`
          & p {
            color: ${light.red100};
          }
          background-color: ${light.red300};
          opacity: 1;
        `
      }
    }}
  }
`

interface MangoButtonProps {
  disabled?: boolean
  onClick?: () => void
  size?: 'normal' | 'small'
  text?: string
  children?: string | React.ReactNode
  type?: 'primary' | 'secondary' | 'positive' | 'negative' | 'warning' | 'white'
  borderShape?: 'rounded' | 'square'
}
/**
 * @deprecated Use @components/shared/MangoButton instead.
 */
export const OldMangoButton = ({
  disabled = false,
  onClick,
  text,
  children,
  type = 'primary',
  size = 'normal',
  borderShape = 'rounded',
}: MangoButtonProps) => {
  const getBody = () => {
    if (text || typeof children === 'string') {
      return (
        <Text
          size={FontStyle.Button}
          text={(text ?? children ?? '') as string}
          align="center"
        />
      )
    }
    return children
  }

  return (
    <StyledButton
      disabled={disabled}
      onClick={onClick}
      size={size}
      type={type}
      borderShape={borderShape}
    >
      {getBody()}
    </StyledButton>
  )
}
